import { Card, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import 'bootstrap-daterangepicker/daterangepicker.css';
import { ModalAllder } from 'layouts/ModalAllder';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Form from 'react-bootstrap/Form';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import AllderTable from '../../../components/AllderTable';
import { useGlobalTable } from '../../../hooks/useGlobalTable';
import { memberHandler } from '../../../redux/handlers';
import { createTableColumns } from '../../../utils/createTableColumns';

export function CustomerHistory() {
  const { customerID, businessSlug } = useParams();
  const dispatch = useDispatch();
  const [memberInfo, setMemberInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [creditModal, setCreditModal] = useState({
    mode: 'normal', open: false, width: 420, height: 280,
    x: 0, y: 0, modalname: 'credit-limit-modal', pin: false,
  });
  const [creditInput, setCreditInput] = useState('');
  const [creditSaving, setCreditSaving] = useState(false);

  const [searchdata, setSearchdata] = useState({
    startDate: moment().format('DD/MM/YYYY'),
    endDate: moment().format('DD/MM/YYYY')
  });

  const fetchMemberHistoryData = useCallback(
    async params => {
      const startDate = moment(searchdata.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      const endDate = moment(searchdata.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      const queryParams = {
        memberId: customerID,
        start_date: startDate,
        end_date: endDate,
        perPage: params.per_page,
        ...params
      };
      return dispatch(memberHandler.getMemberHistoryPagination(null, queryParams));
    },
    [dispatch, customerID, searchdata.startDate, searchdata.endDate]
  );

  const tableDependencies = useMemo(
    () => [searchdata.startDate, searchdata.endDate],
    [searchdata.startDate, searchdata.endDate]
  );

  const {
    data: historyData,
    loading: historyLoading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    debouncedSearch,
    refreshData,
    filterParams
  } = useGlobalTable({
    fetchData: fetchMemberHistoryData,
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultPageSize: 10,
    dependencies: tableDependencies
  });

  const handleExport = useCallback(() => {
    const startDate = moment(searchdata.startDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    const endDate = moment(searchdata.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
    const params = {
      businessSlug: businessSlug,
      memberId: customerID,
      start_date: startDate,
      end_date: endDate,
      s: filterParams.s
    };
    dispatch(memberHandler.getMemberHistoryExcel(null, params));
  }, [dispatch, customerID, searchdata.startDate, searchdata.endDate, filterParams.s]);

  const handleApply = useCallback((event, picker) => {
    const newStartDate = picker.startDate.format('DD/MM/YYYY');
    const newEndDate = picker.endDate.format('DD/MM/YYYY');

    setSearchdata(prev => ({
      ...prev,
      startDate: newStartDate,
      endDate: newEndDate
    }));
  }, []);

  const fetchMemberData = useCallback(
    async memberId => {
      try {
        setLoading(true);
        const response = await dispatch(memberHandler.getMemberById(memberId));
        if (response) {
          setMemberInfo(response);
        }
      } catch (error) {
        console.error('Error fetching member data:', error);
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const handleOpenCreditModal = useCallback(() => {
    setCreditInput(memberInfo.credit_limit_amount != null ? String(memberInfo.credit_limit_amount) : '');
    setCreditModal(prev => ({ ...prev, open: true }));
  }, [memberInfo.credit_limit_amount]);

  const handleSaveCreditLimit = useCallback(async () => {
    const amount = parseFloat(creditInput);
    if (isNaN(amount) || amount < 0) return;
    try {
      setCreditSaving(true);
      await dispatch(memberHandler.updateMemberCreditLimit(businessSlug, customerID, amount));
      await fetchMemberData(customerID);
      setCreditModal(prev => ({ ...prev, open: false }));
    } catch (err) {
      console.error('Error saving credit limit:', err);
    } finally {
      setCreditSaving(false);
    }
  }, [dispatch, customerID, creditInput, fetchMemberData]);

  const calculateMembershipDuration = useCallback(createdDate => {
    if (!createdDate) return '-';

    const created = moment(createdDate);
    const now = moment();
    const years = now.diff(created, 'years');
    const months = now.diff(created.add(years, 'years'), 'months');

    return `${years} ปี ${months} เดือน`;
  }, []);

  const formatDate = useCallback(dateString => {
    if (!dateString) return '-';
    return moment(dateString).format('DD MMM YYYY');
  }, []);

  const formatNumber = useCallback(number => {
    if (!number && number !== 0) return '0';
    return Number(number).toLocaleString();
  }, []);

  const formatCurrency = useCallback(amount => {
    if (!amount && amount !== 0) return '0.00';
    return Number(amount).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }, []);

  const columns = useMemo(
    () =>
      createTableColumns([
        {
          field: 'date',
          headerName: 'วันที่',
          flex: 0.8,
          sortable: false,
          renderCell: ({ row }) => moment(row.created_at).format('DD/MM/YYYY')
        },
        {
          field: 'branchName',
          headerName: 'สาขา',
          flex: 1,
          sortable: false,
          renderCell: ({ row }) => row.order?.branchPosSelling?.branchPos?.branch?.branch_name || ''
        },
        {
          field: 'receipt_number',
          headerName: 'เลขที่ใบเสร็จ',
          flex: 1.2,
          sortable: false,
          renderCell: ({ row }) => (
            <Link
              to={`/${businessSlug}/report/bill/detail/${row.id}`}
              style={{
                color: '#2DC76D',
                fontWeight: 500,
                textDecoration: 'underline'
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {row.receipt_no || '-'}
            </Link>
          )
        },
        {
          field: 'net_total',
          headerName: 'ยอดสุทธิ',
          flex: 0.9,
          sortable: false,
          renderCell: ({ row }) => formatCurrency(row.final_amount)
        },
        {
          field: 'product',
          headerName: 'จำนวนรายการ',
          flex: 0.8,
          sortable: false,
          renderCell: ({ row }) => row.billItems?.length || '-'
        },
        {
          field: 'point_receive',
          headerName: 'คะแนนที่ได้รับ',
          flex: 1,
          sortable: false,
          renderCell: ({ row }) => (
            <div className="text-[#2DC76D] font-bold">{row.additional_data?.point?.earned || '-'}</div>
          )
        },
        {
          field: 'point_used',
          headerName: 'คะแนนที่ใช้',
          flex: 0.9,
          sortable: false,
          renderCell: ({ row }) => (
            <div className="text-[#FF0000] font-bold">{row.additional_data?.point?.spend || '-'}</div>
          )
        },
        {
          field: 'points_remain',
          headerName: 'คะแนนคงเหลือ',
          flex: 1,
          sortable: false,
          renderCell: ({ row }) => (
            <div className="text-[#2DC76D] font-bold">{row.additional_data?.point?.total || '-'}</div>
          )
        },
        {
          field: 'salesperson',
          headerName: 'พนักงานขาย',
          flex: 1.3,
          sortable: false,
          renderCell: ({ row }) => {
            const userBusinessGroup = row.order?.userBusinessGroup;
            if (userBusinessGroup?.first_name || userBusinessGroup?.last_name) {
              const firstName = userBusinessGroup.first_name || '';
              const lastName = userBusinessGroup.last_name || '';
              return `${firstName} ${lastName}`.trim();
            }
            return '-';
          }
        }
      ]),
    [businessSlug, formatCurrency]
  );

  useEffect(() => {
    if (customerID) {
      fetchMemberData(customerID);
    }
  }, [customerID, fetchMemberData]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card card-st-2 pagezoom mb-6">
        <div className="card-body !p-6">
          <div className="grid gap-7" style={{ gridTemplateColumns: 'repeat(13, minmax(0, 1fr))' }}>
            <div className="col-span-2 border-r-[1px] border-[#E5E5E5]">
              <div className="font-bold text-[#636578] text-xl mb-3">
                <div className="font-bold text-[#636578] text-xl mb-3">
                  {memberInfo.entity_structure?.entity_type?.code === 'JURISTIC'
                    ? memberInfo.company_name_th || ''
                    : `${memberInfo.first_name_th || ''} ${memberInfo.last_name_th || ''}`.trim() || ''}
                </div>
              </div>
              <div className="flex mb-3">
                <div className="flex-[0_0_81px] text-end mr-2">รหัสสมาชิก :</div>
                <div className="text-[#2684FC]">{memberInfo.code || '-'}</div>
              </div>
              <div className="flex">
                <div className="flex-[0_0_81px] text-end mr-2">เบอร์โทร :</div>
                <div className="text-[#2684FC]">{memberInfo.phone_number || '-'}</div>
              </div>
            </div>

            <div className="col-span-3 border-r-[1px] border-[#E5E5E5]">
              <div className="flex mb-3">
                <div className="flex-[0_0_150px]">ระยะเวลาเป็นสมาชิก</div>
                <div className="text-[#2DC76D]">{calculateMembershipDuration(memberInfo.created_at)}</div>
              </div>
              <div className="flex mb-3">
                <div className="flex-[0_0_150px]">ระดับสมาชิก</div>
                <div>
                  <span className="rounded-full bg-[#0760FC] px-3 text-white"> - </span>
                </div>
              </div>
              <div className="flex">
                <div className="flex-[0_0_150px]">คะแนนสะสม</div>
                <div>
                  <span className="text-[#2DC76D]">{formatNumber(memberInfo.totalPoint)}</span> คะแนน
                </div>
              </div>
            </div>

            <div className="col-span-2 border-r-[1px] border-[#E5E5E5]">
              <div className="flex items-center gap-2 mb-3">
                <div className="font-bold text-[#636578] text-xl">วงเงินเคดริต</div>
                <IconButton size="small" onClick={handleOpenCreditModal} sx={{ color: '#565DFF' }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </div>
              <div className="mb-2 text-2xl font-bold">
                <span className="text-[#565DFF] mr-3">{formatCurrency(memberInfo.credit_limit_amount ?? 0)}</span>
                <span className="text-[#636578]">บาท</span>
              </div>
              <div className="text-[#636578]">วงเงินที่ได้รับ</div>
            </div>

            <div className="col-span-2 border-r-[1px] border-[#E5E5E5]">
              <div className="font-bold text-[#636578] text-xl mb-3">ยอดซื้อสะสม</div>
              <div className="mb-2 text-2xl font-bold">
                <span className="text-[#2DC76D] mr-3">{formatCurrency(memberInfo.totalSale)}</span>
                <span className="text-[#636578]">บาท</span>
              </div>
              <div className="text-[#636578]">ยอดใช้จ่ายสะสม</div>
            </div>

            <div className="col-span-2 border-r-[1px] border-[#E5E5E5]">
              <div className="font-bold text-[#636578] text-xl mb-3">จำนวนบิลซื้อ</div>
              <div className="mb-2 text-2xl font-bold">
                <span className="text-[#2DC76D] mr-3">{formatNumber(memberInfo.billAmount)}</span>
                <span className="text-[#636578]">บิล</span>
              </div>
              <div className="text-[#636578]">จำนวนครั้งที่มาซื้อสินค้า</div>
            </div>

            <div className="col-span-2">
              <div className="font-bold text-[#636578] text-xl mb-3">สถิติการซื้อ</div>
              <div className="mb-3 px-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-1">ซื้อครั้งแรก</div>
                  <div className="col-span-1 text-[#2DC76D]">
                    {memberInfo.buyFirstAndLatestBill?.first
                      ? formatDate(memberInfo.buyFirstAndLatestBill.first.created_at)
                      : '-'}
                  </div>
                </div>
              </div>
              <div className="px-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-1">ซื้อครั้งล่าสุด</div>
                  <div className="col-span-1 text-[#2DC76D]">
                    {memberInfo.buyFirstAndLatestBill?.latest
                      ? formatDate(memberInfo.buyFirstAndLatestBill.latest.created_at)
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Card className="!rounded-lg">
        <div className="py-4">
          <div className="fs-18 fw-bold me-auto px-4 mb-4" style={{ color: '#767676' }}>
            ข้อมูลการซื้อ
          </div>

          <div className="d-flex justify-content-between align-items-center px-4 mb-3 flex-wrap">
            <div className="d-flex align-items-center mb-2">
              <button className="btn btn-export-st-1" onClick={handleExport}>
                Export
              </button>
            </div>

            <div className="d-flex align-items-center mb-2">
              <div className="w-[300px] ml-auto">
                <Form.Group className="mb-3 form-st-3 pb-1">
                  <Form.Label className="mb-1">เลือกช่วงวันที่</Form.Label>

                  <DateRangePicker
                    initialSettings={{
                      startDate: searchdata.startDate,
                      endDate: searchdata.endDate,
                      locale: {
                        format: 'DD/MM/YYYY'
                      }
                    }}
                    onApply={handleApply}
                  >
                    <input
                      type="text"
                      className="form-control datepicker2 h-[44px]"
                      value={`${searchdata.startDate} - ${searchdata.endDate}`}
                      readOnly
                    />
                  </DateRangePicker>
                </Form.Group>
              </div>

              <div className="mx-4 mb-2">
                <input
                  type="text"
                  className="form-control form-st-4"
                  placeholder="ค้นหาเลขที่ใบเสร็จ"
                  onKeyUp={e => {
                    debouncedSearch(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>

          <AllderTable
            data={historyData}
            loading={historyLoading}
            columns={columns}
            totalRows={totalRows}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            handleSort={handleSort}
          />
        </div>
      </Card>

      {/* ── Modal แก้ไขวงเงินเคดริต ── */}
      <ModalAllder
        modal={creditModal}
        setModal={setCreditModal}
        htitle="แก้ไขวงเงินเคดริต"
        hbtn={true}
      >
        <div className="py-2">
          <Form.Group className="mb-4 form-st-3 pb-1">
            <Form.Label className="mb-1">จำนวนวงเงิน</Form.Label>
            <Form.Control
              type="number"
              min="0"
              placeholder="0.00"
              value={creditInput}
              onChange={e => setCreditInput(e.target.value)}
            />
          </Form.Group>

          <div className="pt-2 btn-group-control text-center">
            <button
              type="button"
              className="btn btn-st-6 me-3"
              style={{ width: 100 }}
              onClick={() => setCreditModal(prev => ({ ...prev, open: false }))}
              disabled={creditSaving}
            >
              ยกเลิก
            </button>
            <button
              type="button"
              className="btn btn-st-3"
              style={{ width: 100 }}
              onClick={handleSaveCreditLimit}
              disabled={creditSaving}
            >
              {creditSaving ? 'กำลังบันทึก...' : 'ยืนยัน'}
            </button>
          </div>
        </div>
      </ModalAllder>
    </div>
  );
}
